import "./PlaygroundWrapper.scss";

import { useMemo, useState } from "react";
import { Accordion } from "react-bootstrap";
import { toast } from "react-toastify";

import { PageTitle } from "../../components/PageTitle";
import { useOrganizationContext } from "../../contexts/organizations/organizationsContext";
import type {
  IError,
  IMessageHeader,
  MessageType,
} from "../../hooks/reactQueryHooks/useProxy";
import { useFetchQuestions } from "../../hooks/reactQueryHooks/useProxy";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import type { ErrorResponse } from "../../utils/PlaygroundUtils";
import { Chat } from "./components/Chat";
import { Code } from "./components/Code";
import { Controls } from "./components/Controls";
import { EmptyState } from "./components/EmptyState";
import { Header } from "./components/Header";
import { QuestionForm } from "./components/QuestionForm";
import { cacheTypes, systemMessage } from "./constants";
import type {
  CacheType,
  IModelOptionType,
  MessagesState,
  OptionType,
  ToolsType,
  updateMessageProps,
} from "./types";

const PlaygroundWrapper = () => {
  // form message state
  const [question, setQuestion] = useState("");

  // controls state
  const [selectedModel, setSelectedModel] = useState<IModelOptionType | null>(
    null,
  );
  const [apiKey, setApiKey] = useState<OptionType | null>(null);
  const [cacheType, setCacheType] = useState<CacheType>(cacheTypes[0]);
  const [seed, setSeed] = useState<number | null>(null);
  const [stream, setStream] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [toolsList, setToolsList] = useState<ToolsType>({});

  // messages state
  const [messages, setMessages] = useState<MessagesState>({
    [systemMessage.id]: { ...systemMessage },
  });
  const { currentOrganization } = useOrganizationContext();
  const isSm = useMediaQuery("lg");
  const { mutate } = useFetchQuestions();

  const handleResponseError = (error: ErrorResponse) => {
    if (error.id) {
      setMessages(prevMessages => {
        const message = prevMessages[error.id || 0];
        return {
          ...prevMessages,
          [error.id || 0]: {
            ...message,
            content: "",
            error: {
              status: 400,
            },
          },
        };
      });
    } else {
      setErrorMessage(400, error.headers);
    }
    setIsLoading(false);
  };

  const onAssistantMessageUpdate = ({
    headers,
    message,
    id,
  }: updateMessageProps) => {
    setMessages(prevMessages => {
      const { choices } = message;
      const messageFromList = prevMessages[id];

      const content =
        choices?.[0]?.delta?.content || choices?.[0]?.message?.content || "";

      const tool_calls =
        choices?.[0]?.delta?.tool_calls ||
        choices?.[0]?.message?.tool_calls ||
        null;

      // default message content
      if (content) {
        const newMessage: MessageType = {
          id,
          headers,
          type: "default",
          message: {
            role: "assistant",
            content: messageFromList?.message?.content
              ? `${messageFromList.message.content}${content}`
              : content,
          },
        };
        return {
          ...prevMessages,
          [id]: newMessage,
        };
      }

      // tool calls for JSON response
      if (choices?.[0]?.message?.tool_calls) {
        const newMessage = {
          id,
          type: "function",
          message: {
            role: "assistant",
            content: "",
            tool_calls,
          },
        };
        const answerMessagesState = tool_calls.reduce((acc: any, tool: any) => {
          return {
            ...acc,
            [tool.id]: {
              id: tool.id,
              type: "functionResult",
              message: {
                role: "tool",
                content: "",
                tool_call_id: tool.id,
              },
            },
          };
        }, {});

        return {
          ...prevMessages,
          [id]: newMessage,
          ...answerMessagesState,
        };
      }

      // tool calls for stream response
      if (choices?.[0]?.delta?.tool_calls) {
        const parsedToolCalls = messageFromList?.message?.tool_calls || [];
        let answerMessagesState: MessageType = {
          id: "",
          type: "functionResult",
          message: {
            role: "tool",
            content: "",
          },
        };

        if (parsedToolCalls[tool_calls[0].index]) {
          parsedToolCalls[tool_calls[0].index].function.arguments = `${
            parsedToolCalls[tool_calls[0].index].function.arguments
          }${tool_calls[0].function.arguments}`;
        } else {
          parsedToolCalls[tool_calls[0].index] = {
            ...tool_calls[0],
          };
          answerMessagesState = {
            id: tool_calls[0].id,
            type: "functionResult",
            message: {
              role: "tool",
              content: "",
              tool_call_id: tool_calls[0].id,
            },
          };
        }

        const newMessage = {
          id,
          type: "function",
          message: {
            role: "assistant",
            content: "",
            tool_calls: parsedToolCalls,
          },
        };

        const state = {
          ...prevMessages,
          [id as string]: newMessage,
        };

        if (answerMessagesState.id) {
          state[answerMessagesState.id] = answerMessagesState;
        }

        return state;
      }

      return prevMessages;
    });

    if (question) {
      setQuestion("");
    }
  };

  const handleCompleteRequest = () => {
    setIsLoading(false);
  };

  // restriction error message
  const setErrorMessage = (status: number, headers?: IMessageHeader) => {
    const newMessage: MessageType = {
      id: Date.now(),
      error: {
        status,
      },
      headers,
      type: "default",
      message: {
        role: "assistant",
        content: "",
      },
    };

    setMessages(prevMessages => {
      return {
        ...prevMessages,
        [newMessage.id]: newMessage,
      };
    });
  };

  // api call error
  const handleError = (error: IError) => {
    if (error?.error?.status === 504 || error?.error?.status === 400) {
      setErrorMessage(error.error.status);
    } else {
      toast.error("Something went wrong! Please try again later.");
    }
    setIsLoading(false);
  };

  const sendQuestion = (question: string | null) => {
    setIsLoading(true);
    let updatedMessages: MessagesState = {
      ...messages,
    };
    if (question) {
      const newMessage: MessageType = {
        id: Date.now(),
        type: "default",
        message: {
          role: "user",
          content: question,
        },
      };
      updatedMessages = {
        ...messages,
        [newMessage.id]: newMessage,
      };
    }

    setMessages(updatedMessages);

    mutate({
      messages: Object.values(updatedMessages)
        .filter(
          ({ message, type }) =>
            !!message.content ||
            type === "function" ||
            type === "functionResult",
        )
        .map(({ message }) => message),
      apiKey: apiKey?.value || "",
      model: selectedModel?.value || "",
      cacheType: cacheType.title,
      seed,
      stream,
      tools: Object.values(toolsList).map(({ tool }) => tool),
      onUpdate: onAssistantMessageUpdate,
      onComplete: handleCompleteRequest,
      onError: handleError,
      onResponseError: handleResponseError,
    });
  };

  const editMessage = (text: string, id: number | string) => {
    const updateMessages = { ...messages };
    updateMessages[id].message.content = text;
    setMessages(updateMessages);
  };

  const deleteMessage = (id: number | string) => {
    setMessages(prevMessages => {
      if (prevMessages[id].type === "function") {
        const toolCalls = prevMessages[id].message.tool_calls;
        const updatedMessages = { ...prevMessages };
        delete updatedMessages[id];
        toolCalls?.forEach((toolCall: any) => {
          delete updatedMessages[toolCall.id];
        });
        return { ...updatedMessages };
      }
      delete prevMessages[id];
      return { ...prevMessages };
    });
  };

  const resetState = () => {
    setQuestion("");
    setMessages({ [systemMessage.id]: messages[systemMessage.id] });
  };

  const addQuestion = (question: string) => {
    const newMessage: MessageType = {
      id: Date.now(),
      type: "default",
      message: {
        role: "user",
        content: question,
      },
    };

    setMessages((prevMessages: MessagesState) => {
      return {
        ...prevMessages,
        [newMessage.id]: newMessage,
      };
    });
    setQuestion("");
  };

  const editFunctionResultMessage = (message: MessageType) => {
    setMessages(prevMessages => {
      return {
        ...prevMessages,
        [message.id]: message,
      };
    });
  };

  const hasEmptyFunction = useMemo(() => {
    const functionResults = Object.values(messages).filter(
      ({ type }) => type === "functionResult",
    );
    return functionResults.length
      ? functionResults.some(({ message }) => !message.content.trim())
      : false;
  }, [messages]);

  return (
    <>
      <div className="d-flex justify-content-between align-items-center no-breadcrumbs">
        <PageTitle pageTitle="Playground" />
        <Code
          model={selectedModel?.value}
          messages={messages}
          isStream={stream}
          seed={seed}
          tools={
            selectedModel?.withTools
              ? Object.values(toolsList).map(({ tool }) => tool)
              : null
          }
          cacheControl={cacheType.title}
        />
      </div>
      <div className="playground-wrapper">
        <div className="card flex-shrink-1 flex-grow-1 h-100">
          <div className="d-flex flex-column h-100">
            <Header />
            {Object.values(messages).length === 1 ? (
              <EmptyState />
            ) : (
              <Chat
                messages={messages}
                isLoading={isLoading}
                deleteMessage={deleteMessage}
                editMessage={editMessage}
                editFunctionResultMessage={editFunctionResultMessage}
                sendQuestion={sendQuestion}
                disabledSubmit={hasEmptyFunction}
              />
            )}
            <QuestionForm
              isLoading={isLoading}
              isEmptyState={Object.values(messages).length === 1}
              hasApiKey={!!apiKey}
              sendQuestion={sendQuestion}
              resetState={resetState}
              addQuestion={addQuestion}
              disabledSubmit={hasEmptyFunction}
            />
          </div>
        </div>
        {!isSm ? (
          <Controls
            selectedModel={selectedModel}
            apiKey={apiKey}
            organizationId={currentOrganization?.id || ""}
            cacheType={cacheType}
            systemMessage={messages[systemMessage.id]}
            seed={seed}
            stream={stream}
            selectedTools={toolsList}
            setCacheType={setCacheType}
            setApiKey={setApiKey}
            setSelectedModel={setSelectedModel}
            editMessage={editMessage}
            setSeed={setSeed}
            setStream={setStream}
            setSelectedTools={setToolsList}
          />
        ) : (
          <Accordion className="playground-collapse">
            <Accordion.Item eventKey="0">
              <Accordion.Header>Settings</Accordion.Header>
              <Accordion.Body>
                <Controls
                  selectedModel={selectedModel}
                  apiKey={apiKey}
                  organizationId={currentOrganization?.id || ""}
                  cacheType={cacheType}
                  systemMessage={messages[systemMessage.id]}
                  seed={seed}
                  stream={stream}
                  selectedTools={toolsList}
                  setCacheType={setCacheType}
                  setApiKey={setApiKey}
                  setSelectedModel={setSelectedModel}
                  editMessage={editMessage}
                  setSeed={setSeed}
                  setStream={setStream}
                  setSelectedTools={setToolsList}
                />
              </Accordion.Body>
            </Accordion.Item>
          </Accordion>
        )}
      </div>
    </>
  );
};

export { PlaygroundWrapper };
